import { Injectable } from '@angular/core';
import type { EchartsConfig, ILaneConfig, LaneConfig, ZoomSliderMode } from 'commons';
import type { GridComponentOption } from 'echarts';
import { CHART_CONSTANTS, type GridLayoutConfig } from './echarts-config.types';

/**
 * Service responsible for building grid configurations for ECharts multilane charts.
 * Handles the layout and positioning of grid components based on zoom mode and lane count.
 */
@Injectable({ providedIn: 'root' })
export class GridBuilderService {
  /**
   * Builds grid configuration for all lanes
   */
  buildGrids(
    lanes: LaneConfig[] | ILaneConfig[],
    zoomSliderMode: ZoomSliderMode,
    gridHeight: number = CHART_CONSTANTS.DEFAULT_GRID_HEIGHT,
    config?: EchartsConfig
  ): GridComponentOption[] {
    const layoutConfig = this.extractLayoutConfig(config);

    if (lanes.length === 0) {
      return [this.createDefaultGrid(zoomSliderMode, gridHeight, layoutConfig)];
    }

    return lanes.map((lane, index) => this.createGrid(index, zoomSliderMode, gridHeight, layoutConfig, config, lane));
  }

  /**
   * Calculates the top position of a grid based on its index and zoom mode.
   * Overloaded to accept either full EchartsConfig or GridLayoutConfig.
   *
   * Layout calculation:
   * - LANE mode: Each lane has its own zoom slider, adding extra vertical space
   * - GLOBAL mode: Single zoom slider at the top
   */
  calculateGridTopPosition(gridIndex: number, zoomSliderMode: ZoomSliderMode, config?: EchartsConfig | GridLayoutConfig): number {
    const gridHeight = (config as EchartsConfig)?.gridHeight ?? CHART_CONSTANTS.DEFAULT_GRID_HEIGHT;
    const gapBase = config?.gridGapBase ?? CHART_CONSTANTS.GRID_GAP_BASE;
    const laneOffset = config?.gridLaneOffsetValue ?? CHART_CONSTANTS.LANE_OFFSET_VALUE;
    const zoomOffset = config?.gridZoomLevelOffset ?? CHART_CONSTANTS.ZOOM_LEVEL_OFFSET;

    const isLaneMode = zoomSliderMode === 'lane';
    const zoomSliderSpacing = isLaneMode ? CHART_CONSTANTS.ZOOM_SLIDER_HEIGHT : 0;

    const basePosition = gridIndex * (gridHeight + gapBase + zoomSliderSpacing);
    let initialOffset: number;
    if (isLaneMode) {
      initialOffset = gridIndex === 0 ? 20 : laneOffset;
    } else {
      initialOffset = zoomOffset;
    }

    return basePosition + initialOffset;
  }

  /**
   * Calculates the effective grid height based on lane count
   */
  calculateEffectiveGridHeight(laneCount: number): number {
    return (laneCount === 1 ? 2 : 1) * CHART_CONSTANTS.GRID_BASE_HEIGHT;
  }

  /**
   * Gets the grid gap for lane zoom mode
   */
  getGridGapForLaneZoom(): number {
    return CHART_CONSTANTS.GRID_GAP_LANE_ZOOM;
  }

  /**
   * Gets the base grid gap
   */
  getGridGapBase(): number {
    return CHART_CONSTANTS.GRID_GAP_BASE;
  }

  private createGrid(
    index: number,
    zoomSliderMode: ZoomSliderMode,
    gridHeight: number,
    layoutConfig: GridLayoutConfig,
    config?: EchartsConfig,
    lane?: LaneConfig | ILaneConfig
  ): GridComponentOption {
    const hasRightAxis = lane?.config?.yAxises?.some((y) => y.settings?.position === 'right');
    const rightPadding =
      layoutConfig.gridRightPadding ?? (hasRightAxis ? CHART_CONSTANTS.DEFAULT_GRID_LEFT_PADDING : CHART_CONSTANTS.DEFAULT_GRID_RIGHT_PADDING);

    return {
      id: `grid-${index}`,
      left: layoutConfig.gridLeftPadding ?? CHART_CONSTANTS.DEFAULT_GRID_LEFT_PADDING,
      right: rightPadding,
      height: gridHeight,
      top: this.calculateGridTopPosition(index, zoomSliderMode, config),
      width: this.formatWidthPercentage(layoutConfig.gridWidthPercentage),
      show: config?.gridShow ?? true,
      backgroundColor: config?.gridBackgroundColor ?? 'transparent',
      borderColor: config?.gridBorderColor ?? '#ccc',
      borderWidth: config?.gridShowBorder ? (config?.gridBorderWidth ?? 1) : 0,
      containLabel: config?.gridContainLabel ?? false,
    };
  }

  private createDefaultGrid(zoomSliderMode: ZoomSliderMode, gridHeight: number, config: GridLayoutConfig): GridComponentOption {
    return {
      id: 'grid-0',
      left: config.gridLeftPadding ?? CHART_CONSTANTS.DEFAULT_GRID_LEFT_PADDING,
      right: config.gridRightPadding ?? CHART_CONSTANTS.DEFAULT_GRID_RIGHT_PADDING,
      height: gridHeight,
      top: this.calculateGridTopPosition(1, zoomSliderMode, config),
    };
  }

  private extractLayoutConfig(config?: EchartsConfig): GridLayoutConfig {
    if (!config) {
      return {};
    }

    return {
      gridGapBase: config.gridGapBase,
      gridLeftPadding: config.gridLeftPadding,
      gridRightPadding: config.gridRightPadding,
      gridZoomLevelOffset: config.gridZoomLevelOffset,
      gridLaneOffsetValue: config.gridLaneOffsetValue,
      gridWidthPercentage: config.gridWidthPercentage,
    };
  }

  private formatWidthPercentage(percentage?: number): string {
    return percentage ? `${percentage}%` : `${CHART_CONSTANTS.DEFAULT_GRID_WIDTH_PERCENTAGE}%`;
  }
}
